  import { supabase } from '../config/supabase'
  import { validateDateOfBirth } from '../utils/validation'

  /**
   * Web Authentication Service
   * Handles user authentication for customers
   * 
   * All customers use role = "customer" (unified role for web and mobile)
   */

  /**
   * Check if a user is an admin by checking the admins table
   * @param {string} userId - User ID to check
   * @returns {Promise<boolean>} True if user is an active admin
   */
  export const isUserAdmin = async (userId) => {
    try {
      if (!userId) {
        return false;
      }

      // Check if user exists in admins table and is active
      const { data, error } = await supabase
        .from('admins')
        .select('id, is_active')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.warn('Error checking admin status:', error);
        return false;
      }

      const isAdmin = !!data;
      console.log('🔍 isUserAdmin - User ID:', userId, 'Is Admin:', isAdmin);
      return isAdmin;
    } catch (error) {
      console.error('Error in isUserAdmin:', error);
      return false;
    }
  };

  /**
   * Sign up a new customer
   * Automatically creates profile with role = "customer"
   * 
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User email
   * @param {string} userData.password - User password
   * @param {string} userData.firstName - User first name
   * @param {string} userData.lastName - User last name
   * @param {string} userData.mobile - User mobile number
   * @param {string} userData.dateOfBirth - User date of birth (YYYY-MM-DD)
   * @param {string} userData.gender - User gender
   * @returns {Promise<Object>} Sign up result
   */
  export const signUpWebCustomer = async (userData) => {
    try {
      const { password, firstName, lastName, mobile, dateOfBirth, gender, countryCode } = userData
      const email = String(userData.email || '').trim().toLowerCase()
      if (!email) {
        return { success: false, error: 'Email is required.' }
      }
      
      const dobValidation = validateDateOfBirth(dateOfBirth, { minAge: 0, required: true })
      if (!dobValidation.isValid) {
        return { success: false, error: dobValidation.message }
      }

      const emailRedirectTo = `${window.location.origin}/auth/callback`

      // Sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: 'customer',
            first_name: firstName,
            last_name: lastName,
            mobile: mobile,
            country_code: countryCode,
            date_of_birth: dateOfBirth,
            gender: gender,
            account_status: 'active',
          },
          emailRedirectTo,
        },
      })

      if (authError) {
        console.error('Sign up auth error:', authError)
        const errorMessage = (authError.message || '').toLowerCase()
        if (
          errorMessage.includes('confirmation email') ||
          errorMessage.includes('sending confirmation') ||
          errorMessage.includes('unable to send') ||
          errorMessage.includes('error sending')
        ) {
          return {
            success: false,
            error: 'Could not send the confirmation email. Please try again in a few minutes.',
          }
        }
        if (errorMessage.includes('rate limit') || errorMessage.includes('too many') || authError.status === 429) {
          return {
            success: false,
            error: 'Too many emails were sent. Please wait a few minutes and try again.',
          }
        }
        if (
          errorMessage.includes('already registered') ||
          errorMessage.includes('already exists') ||
          errorMessage.includes('user already registered')
        ) {
          // Duplicate signup never re-sends mail — resend the OTP explicitly.
          const resent = await resendSignupOtp(email)
          return {
            success: false,
            error: resent.success
              ? 'This email is already registered. We sent a new 6-digit code — enter it to continue, or sign in if you already verified.'
              : 'This email is already registered. Please sign in, or use Resend code on the next screen.',
            alreadyRegistered: true,
            needsEmailConfirmation: true,
            emailResent: !!resent.success,
          }
        }
        return { success: false, error: authError.message }
      }

      if (!authData.user) {
        return { success: false, error: 'No user data returned from sign up' }
      }

      const alreadyRegistered =
        Array.isArray(authData.user.identities) && authData.user.identities.length === 0
      if (alreadyRegistered) {
        // Supabase returns a fake success with empty identities and does NOT email again.
        const resent = await resendSignupOtp(email)
        return {
          success: false,
          error: resent.success
            ? 'This email is already registered. We sent a new 6-digit code — enter it to continue, or sign in if you already verified.'
            : 'This email is already registered. Please sign in, or use Resend code on the next screen.',
          alreadyRegistered: true,
          needsEmailConfirmation: true,
          emailResent: !!resent.success,
        }
      }

      // Confirm email is ON: no session until the 6-digit code is verified.
      // Do not write profiles here — RLS blocks it, which left empty rows.
      if (!authData.session) {
        // If Supabase created the user but did not queue a confirmation email, force a resend.
        if (!authData.user.confirmation_sent_at) {
          const resent = await resendSignupOtp(email)
          if (!resent.success) {
            return {
              success: false,
              error: resent.error || 'Could not send the confirmation email. Please try again in a few minutes.',
            }
          }
        }
        return {
          success: true,
          user: authData.user,
          session: null,
          needsEmailConfirmation: true,
          message: 'Account created. Enter the 6-digit code we sent to your email.',
        }
      }

      // Wait a moment for potential trigger to execute
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Check if profile was created by trigger
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', authData.user.id)
        .maybeSingle()

      // If profile doesn't exist, create it manually with ALL user data
      if (!existingProfile) {
        console.log('Profile not created by trigger, creating manually...')
        
        const profileData = {
          id: authData.user.id,
          email: email,
          first_name: firstName,
          last_name: lastName,
          mobile: mobile || null,
              country_code: countryCode || '+1', // Default to +1 if not provided
          date_of_birth: dateOfBirth || null,
          gender: gender || null,
          role: 'customer',
          account_status: 'active', // Set account as active on signup
          currency: 'USD', // Default currency
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }

        const { error: profileError } = await supabase
          .from('profiles')
          .insert(profileData)

        if (profileError) {
          console.error('Error creating profile:', profileError)
          // Try to update if profile exists but wasn't found (race condition)
          const { error: updateError } = await supabase
            .from('profiles')
            .update({
              first_name: firstName,
              last_name: lastName,
              mobile: mobile || null,
              country_code: countryCode,
              date_of_birth: dateOfBirth || null,
              gender: gender || null,
              role: 'customer',
              account_status: 'active',
              updated_at: new Date().toISOString(),
            })
            .eq('id', authData.user.id)

          if (updateError) {
            console.error('Error updating profile:', updateError)
            // Don't fail signup if profile creation/update fails
          // User can still login and profile will be created later
          } else {
            console.log('✅ Profile updated successfully for web customer')
          }
        } else {
          console.log('✅ Profile created successfully with all user data:', {
            email,
            firstName,
            lastName,
            mobile,
            dateOfBirth,
            gender,
            role: 'web_customer',
            account_status: 'active'
          })
        }
      } else {
        // Profile exists - ensure all data is up to date
        console.log('✅ Profile already exists (created by trigger), verifying data...')
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            first_name: firstName,
            last_name: lastName,
            mobile: mobile || null,
            country_code: countryCode,
            date_of_birth: dateOfBirth || null,
            gender: gender || null,
            role: 'customer',
            account_status: 'active',
            updated_at: new Date().toISOString(),
          })
          .eq('id', authData.user.id)

        if (updateError) {
          console.warn('Warning: Could not update existing profile:', updateError)
        } else {
          console.log('✅ Profile data updated successfully')
        }
      }

      return {
        success: true,
        user: authData.user,
        session: authData.session,
        needsEmailConfirmation: !authData.session,
        message: authData.session
          ? 'Account created successfully!'
          : 'Account created. Enter the 6-digit code we sent to your email.',
      }
    } catch (error) {
      console.error('Unexpected error during sign up:', error)
      return { success: false, error: 'An unexpected error occurred during sign up' }
    }
  }

  const mapSignupOtpError = (error) => {
    const msg = (error?.message || '').toLowerCase()
    if (msg.includes('expired') || msg.includes('invalid') || msg.includes('otp')) {
      return 'Invalid or expired code. Please request a new one.'
    }
    if (msg.includes('rate limit') || msg.includes('too many')) {
      return 'Too many attempts. Please wait a few minutes and try again.'
    }
    return error?.message || 'Could not verify the code. Please try again.'
  }

  /** Confirm signup with the 6-digit code from the confirmation email. */
  export const completeSignupProfile = async (profileInput = {}, userOverride = null) => {
    try {
      const user = userOverride || (await supabase.auth.getUser()).data?.user
      if (!user) {
        return { success: false, error: 'Not authenticated' }
      }

      const meta = user.user_metadata || {}
      const firstName = profileInput.firstName || meta.first_name || ''
      const lastName = profileInput.lastName || meta.last_name || ''
      const mobile = profileInput.mobile || meta.mobile || ''
      const email = profileInput.email || user.email || ''
      const dateOfBirth = profileInput.dateOfBirth || meta.date_of_birth || null
      const gender = profileInput.gender || meta.gender || null
      const countryCode = profileInput.countryCode || meta.country_code || '+1'

      const { data: rpcResult, error: rpcError } = await supabase.rpc(
        'create_or_update_profile_on_signup',
        {
          p_user_id: user.id,
          p_first_name: firstName,
          p_last_name: lastName,
          p_mobile: mobile,
          p_email: email,
          p_date_of_birth: dateOfBirth || null,
          p_gender: gender,
          p_country_code: countryCode,
        }
      )

      if (!rpcError && rpcResult?.success) {
        return { success: true, profile: rpcResult.profile }
      }

      const payload = {
        id: user.id,
        first_name: firstName,
        last_name: lastName,
        mobile: mobile || null,
        email,
        date_of_birth: dateOfBirth || null,
        gender: gender || null,
        country_code: countryCode,
        role: 'customer',
        account_status: 'active',
        updated_at: new Date().toISOString(),
      }

      const { data, error } = await supabase
        .from('profiles')
        .upsert(payload, { onConflict: 'id' })
        .select()
        .maybeSingle()

      if (error) {
        console.error('Error saving signup profile:', error)
        return { success: false, error: error.message }
      }

      return { success: true, profile: data }
    } catch (error) {
      console.error('completeSignupProfile error:', error)
      return { success: false, error: error?.message || 'Failed to save profile' }
    }
  }

  export const verifySignupOtp = async (email, token, profileOverride = {}) => {
    if (!email?.trim() || !token?.trim()) {
      return { success: false, error: 'Email and verification code are required.' }
    }

    const normalizedEmail = email.trim().toLowerCase()
    const normalizedToken = token.trim()

    let { data, error } = await supabase.auth.verifyOtp({
      email: normalizedEmail,
      token: normalizedToken,
      type: 'signup',
    })

    if (error) {
      const fallback = await supabase.auth.verifyOtp({
        email: normalizedEmail,
        token: normalizedToken,
        type: 'email',
      })
      data = fallback.data
      error = fallback.error
    }

    if (error) {
      return { success: false, error: mapSignupOtpError(error) }
    }

    await completeSignupProfile(profileOverride, data?.user)

    return { success: true, user: data?.user ?? null, session: data?.session ?? null }
  }

  /** Resend the 6-digit signup confirmation code. */
  export const resendSignupOtp = async (email) => {
    if (!email?.trim()) {
      return { success: false, error: 'Email is required.' }
    }

    const normalized = email.trim().toLowerCase()
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: normalized,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) {
      const msg = (error.message || '').toLowerCase()
      if (msg.includes('rate limit') || msg.includes('too many') || error.status === 429) {
        return { success: false, error: 'Too many emails were sent. Please wait a few minutes and try again.' }
      }
      if (msg.includes('confirmation email') || msg.includes('sending') || msg.includes('unable to send')) {
        return { success: false, error: 'Could not send the confirmation code. Please try again in a few minutes.' }
      }
      return { success: false, error: error.message || 'Failed to resend the code.' }
    }

    return { success: true }
  }

  /**
   * Sign in a customer
   * 
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Sign in result with user and role info
   */
  export const signInWebCustomer = async (email, password) => {
    try {
      // Attempt sign in
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (authError) {
        console.error('Sign in error:', authError)
        const errorCode = authError.code || ''
        const errorMessage = (authError.message || '').toLowerCase()
        if (errorCode === 'email_not_confirmed' || errorMessage.includes('email not confirmed')) {
          return {
            success: false,
            error: 'Please confirm your email first. Enter the 6-digit code we sent, then try signing in again.',
            isEmailNotConfirmed: true,
          }
        }
        return { success: false, error: authError.message }
      }

      if (!authData.user) {
        return { success: false, error: 'No user data returned' }
      }

      const userId = authData.user.id

      // IMPORTANT: Check admin status FIRST, before any other checks
      // This prevents AuthContext from processing the session before we can return the error
      const isAdmin = await isUserAdmin(userId);
      
      console.log('🔍 signInWebCustomer - Is Admin (from admins table):', isAdmin)
      console.log('🔍 signInWebCustomer - User ID:', userId)

      // Block admin users from customer dashboard
      // Check admins table FIRST (this is the authoritative source)
      if (isAdmin) {
        console.log('🚫 BLOCKING admin user from customer dashboard')
        // Sign out immediately to prevent AuthContext from processing the session
        return {
          success: false,
          error: 'Admin accounts must use admin panel',
          isRoleBlocked: true,
        }
      }

      // Fetch profile status and role from database (source of truth)
      let profileStatus = 'active'
      let profileRole = null
      try {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('role, account_status')
          .eq('id', userId)
          .maybeSingle()
        
        if (profileData) {
          profileRole = profileData.role
          profileStatus = profileData.account_status || 'active'
        }
        console.log('🔍 signInWebCustomer - Profile role from DB:', profileRole)
        console.log('🔍 signInWebCustomer - Profile status from DB:', profileStatus)
      } catch (profileError) {
        console.warn('Unable to fetch profile from profiles table:', profileError)
      }

      // Check account status - check BOTH metadata AND database profile
      const metadataStatus = authData.user.user_metadata?.account_status || 
                            authData.user.raw_user_meta_data?.account_status || 
                            null
      
      const accountStatus = profileStatus || metadataStatus || 'active'

      // If account is inactive, sign out and return error (user must contact admin)
      if (accountStatus === 'inactive') {
        await supabase.auth.signOut()
        return {
          success: false,
          error: 'Your account has been deactivated. Please contact the administrator to reactivate your account.',
          isInactive: true,
          userId: userId,
        }
      }

      // Get user role - check BOTH metadata AND database profile (database is source of truth!)
      const metadataRole = authData.user.user_metadata?.role || 
                          authData.user.raw_user_meta_data?.role || 
                          null

      console.log('🔍 signInWebCustomer - Profile role (DB):', profileRole)
      console.log('🔍 signInWebCustomer - Metadata role:', metadataRole)

      // Fetch or create profile
      let profile = await getOrCreateProfile(authData.user)

      // If no role found, default to 'customer' (only after admin check passed)
      const finalRole = profileRole || metadataRole || 'customer';
      
      // Normalize role: treat both 'customer' and 'web_customer' as 'customer'
      const normalizedRole = (finalRole === 'web_customer' || finalRole === 'customer') ? 'customer' : finalRole

      return {
        success: true,
        user: authData.user,
        session: authData.session,
        profile: profile,
        role: normalizedRole,
      }
    } catch (error) {
      console.error('Unexpected error during sign in:', error)
      return { success: false, error: 'An unexpected error occurred during sign in' }
    }
  }

  /**
   * Sign in with Google OAuth for customers
   * Sets role = "customer" in metadata
   * 
   * @returns {Promise<Object>} OAuth sign in result
   */
export const signInWithGoogleWeb = async () => {
  try {
    // IMPORTANT: We use PKCE flow (see `supabase.js`), so Supabase will redirect back with `?code=...`
    // and we must exchange it for a session on `/auth/callback`.
    const redirectTo = `${window.location.origin}/auth/callback`

    console.log('🔐 Initiating Google OAuth sign in...')
    console.log('📍 Redirect URL:', redirectTo)
    console.log('⚠️ IMPORTANT: Make sure this EXACT URL is added to Supabase Dashboard:')
    console.log('   1. Go to Supabase Dashboard > Authentication > URL Configuration')
    console.log('   2. Under "Redirect URLs", add:', redirectTo)
    console.log('   3. Make sure Site URL is set to:', window.location.origin)
    console.log('   4. Save the configuration')
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // Must match the redirect URL configured in Supabase Auth settings
        redirectTo,
      },
    })

      if (error) {
        console.error('❌ Google sign in error:', error)
        return { success: false, error: error.message }
      }

      console.log('✅ Google OAuth redirect initiated')
      console.log('📝 After Google authentication, you will be redirected back to:', redirectTo)
      console.log('📝 With PKCE, the URL will include a code like: /auth/callback?code=...')
      return { success: true, data }
    } catch (error) {
      console.error('❌ Unexpected error during Google sign in:', error)
      return { success: false, error: 'An unexpected error occurred during Google sign in' }
    }
  }

  /**
   * Sign in with Facebook OAuth for customers
   * Sets role = "customer" in metadata
   * 
   * @returns {Promise<Object>} OAuth sign in result
   */
export const signInWithFacebookWeb = async () => {
  try {
    const redirectTo = `${window.location.origin}/auth/callback`
    
    console.log('🔐 Initiating Facebook OAuth sign in...')
    console.log('📍 Redirect URL:', redirectTo)
    console.log('⚠️ IMPORTANT: Make sure this URL is added to Supabase Dashboard > Authentication > URL Configuration > Redirect URLs')
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo,
      },
    })

      if (error) {
        console.error('❌ Facebook sign in error:', error)
        return { success: false, error: error.message }
      }

      console.log('✅ Facebook OAuth redirect initiated')
      console.log('📝 After Facebook authentication, you will be redirected back to:', redirectTo)
      return { success: true, data }
    } catch (error) {
      console.error('❌ Unexpected error during Facebook sign in:', error)
      return { success: false, error: 'An unexpected error occurred during Facebook sign in' }
    }
  }

/**
 * Process OAuth callback
 * NOTE: This function is deprecated. Supabase handles OAuth automatically with detectSessionInUrl: true
 * AuthContext will detect the session via getSession() on app startup
 * 
 * @returns {Promise<Object>} OAuth callback result
 */
export const processOAuthCallbackWeb = async () => {
  try {
    // Supabase automatically processes OAuth tokens from URL with detectSessionInUrl: true
    // Just verify session exists
    const { data: { session } } = await supabase.auth.getSession()

    if (!session?.user) {
      return { success: false, error: 'No active session' }
    }

    return {
      success: true,
      user: session.user,
      session,
      role: session.user.user_metadata?.role || 'customer',
    }
  } catch (error) {
    console.error('processOAuthCallbackWeb error:', error)
    return { success: false, error: 'OAuth processing skipped' }
  }
}

  /**
   * Get or create profile for authenticated user
   * Ensures profile exists in database with correct fields
   * 
   * @param {Object} user - Supabase auth user object
   * @returns {Promise<Object|null>} User profile or null
   */
  export const getOrCreateProfile = async (user) => {
    try {
      if (!user) return null

      // Try to fetch existing profile
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()

      // Get metadata from both user_metadata and raw_user_meta_data (Google OAuth stores in both)
      const metadata = user.user_metadata || {}
      const rawMetadata = user.raw_user_meta_data || {}
      
      // Google OAuth provides: given_name, family_name, name, picture
      // Check for Google OAuth fields first (given_name, family_name)
      const firstName = 
        metadata.given_name || 
        rawMetadata.given_name || 
        metadata.first_name || 
        rawMetadata.first_name || 
        ''
      
      const lastName = 
        metadata.family_name || 
        rawMetadata.family_name || 
        metadata.last_name || 
        rawMetadata.last_name || 
        ''
      
      // If we don't have first/last name separately, try to parse from full name
      let finalFirstName = firstName
      let finalLastName = lastName
      
      if (!finalFirstName && !finalLastName) {
        const fullName = 
          metadata.full_name || 
          rawMetadata.full_name || 
          metadata.name || 
          rawMetadata.name || 
          ''
        
        if (fullName) {
          const nameParts = fullName.split(' ').filter(Boolean)
          finalFirstName = nameParts[0] || ''
          finalLastName = nameParts.slice(1).join(' ') || ''
        }
      }

      const metaMobile = metadata.mobile || rawMetadata.mobile || metadata.phone || rawMetadata.phone || ''
      const metaCountryCode = metadata.country_code || rawMetadata.country_code || metadata.countryCode || rawMetadata.countryCode || ''
      const metaDob = metadata.date_of_birth || rawMetadata.date_of_birth || ''
      const metaGender = metadata.gender || rawMetadata.gender || ''
      const metaEmail = user.email || metadata.email || rawMetadata.email || ''

      // If profile exists but is missing signup/OAuth fields, update it
      if (existingProfile) {
        const needsUpdate = (!existingProfile.first_name && finalFirstName) || 
                           (!existingProfile.last_name && finalLastName) ||
                           (!existingProfile.mobile && metaMobile) ||
                           (!existingProfile.country_code && metaCountryCode) ||
                           (!existingProfile.date_of_birth && metaDob) ||
                           (!existingProfile.gender && metaGender) ||
                           (!existingProfile.email && metaEmail) ||
                           (!existingProfile.profile_image_url && (metadata.picture || rawMetadata.picture))
        
        if (needsUpdate) {
          const updateData = {}
          if (!existingProfile.first_name && finalFirstName) {
            updateData.first_name = finalFirstName
          }
          if (!existingProfile.last_name && finalLastName) {
            updateData.last_name = finalLastName
          }
          if (!existingProfile.mobile && metaMobile) {
            updateData.mobile = metaMobile
          }
          if (!existingProfile.country_code && metaCountryCode) {
            updateData.country_code = metaCountryCode
          }
          if (!existingProfile.date_of_birth && metaDob) {
            updateData.date_of_birth = metaDob
          }
          if (!existingProfile.gender && metaGender) {
            updateData.gender = metaGender
          }
          if (!existingProfile.email && metaEmail) {
            updateData.email = metaEmail
          }
          if (!existingProfile.profile_image_url && (metadata.picture || rawMetadata.picture)) {
            updateData.profile_image_url = metadata.picture || rawMetadata.picture
          }
          
          if (Object.keys(updateData).length > 0) {
            updateData.updated_at = new Date().toISOString()
            
            const { data: updatedProfile, error: updateError } = await supabase
              .from('profiles')
              .update(updateData)
              .eq('id', user.id)
              .select()
              .single()
            
            if (updateError) {
              console.warn('⚠️ Failed to update profile with OAuth data:', updateError)
              return existingProfile
            }
            
            console.log('✅ Profile updated with OAuth data')
            return updatedProfile
          }
        }
        
        return existingProfile
      }

      // Profile doesn't exist - create it
      console.log('Profile not found, creating for user:', user.id)

      console.log('📝 Extracted name from OAuth metadata:', {
        firstName: finalFirstName,
        lastName: finalLastName,
        metadata: metadata,
        rawMetadata: rawMetadata
      })

      const profileData = {
        id: user.id,
        email: user.email || metadata.email || rawMetadata.email || '',
        first_name: finalFirstName,
        last_name: finalLastName,
        mobile: metadata.mobile || rawMetadata.mobile || metadata.phone || rawMetadata.phone || '',
        date_of_birth: metadata.date_of_birth || rawMetadata.date_of_birth || null,
        gender: metadata.gender || rawMetadata.gender || null,
        profile_image_url: metadata.avatar_url || rawMetadata.avatar_url || metadata.picture || rawMetadata.picture || null,
        role: 'customer',
        currency: 'USD',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }

      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert(profileData)
        .select()
        .single()

      if (createError) {
        console.error('Error creating profile:', createError)
        
        // If it's a 401 (unauthorized), it's likely an RLS policy issue
        // Try to fetch the profile again in case it was created by a trigger
        if (createError.code === 'PGRST301' || createError.status === 401) {
          console.log('⚠️ Profile insert failed (401), checking if profile was created by trigger...')
          try {
            const { data: existingProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', user.id)
              .maybeSingle()
            
            if (existingProfile) {
              console.log('✅ Profile found after insert error (likely created by trigger)')
              return existingProfile
            }
          } catch (fetchError) {
            console.warn('Error fetching profile after insert failure:', fetchError)
          }
        }
        
        return null
      }

      console.log('✅ Profile created successfully')
      return newProfile
    } catch (error) {
      console.error('Error in getOrCreateProfile:', error)
      return null
    }
  }

  /**
   * Get current user profile with role information
   * 
   * @returns {Promise<Object|null>} User profile or null
   */
  export const getCurrentWebProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) return null

      const profile = await getOrCreateProfile(user)
      return profile
    } catch (error) {
      console.error('Error getting current profile:', error)
      return null
    }
  }

  /**
   * Sign out current user
   * 
   * @returns {Promise<Object>} Sign out result
   */
  export const signOutWeb = async () => {
    console.log('🔐 signOutWeb called - clearing local session only (web session independent)')
    try {
      // Get Supabase project ref from URL to identify session keys
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const projectRef = supabaseUrl ? supabaseUrl.split('//')[1]?.split('.')[0] : null
      
      // Clear Supabase session from localStorage (doesn't invalidate server session)
      // This ensures the web app appears logged out locally, but doesn't affect the app session
      try {
        // Supabase stores session with keys like: sb-<project-ref>-auth-token
        const allKeys = Object.keys(localStorage)
        const supabaseKeys = allKeys.filter(key => {
          if (projectRef && key.includes(projectRef)) return true
          if (key.startsWith('sb-')) return true
          if (key.includes('supabase')) return true
          return false
        })
        
        // Remove Supabase session keys
        supabaseKeys.forEach(key => {
          localStorage.removeItem(key)
          console.log('🗑️ Removed Supabase key:', key)
        })
        
        // Also clear sessionStorage Supabase keys
        const sessionKeys = Object.keys(sessionStorage)
        const supabaseSessionKeys = sessionKeys.filter(key => {
          if (projectRef && key.includes(projectRef)) return true
          if (key.startsWith('sb-')) return true
          if (key.includes('supabase')) return true
          return false
        })
        supabaseSessionKeys.forEach(key => sessionStorage.removeItem(key))
      
        // Clear other app-specific storage (but keep Supabase session on server)
        localStorage.removeItem('user')
        localStorage.removeItem('favorites')
        localStorage.removeItem('bookings')
        localStorage.removeItem('readNotifications')
        localStorage.removeItem('submittedReviewsCache')
      } catch (storageError) {
        console.warn('⚠️ Error clearing Supabase storage:', storageError)
        // Fallback: clear all if specific clearing fails
        localStorage.clear()
        sessionStorage.clear()
      }

      console.log('✅ Web session cleared (local only - app session remains active)')
      return { success: true }
    } catch (error) {
      console.error('❌ Unexpected error during sign out:', error)
      // Still try to clear storage even on error
      try {
        localStorage.clear()
        sessionStorage.clear()
      } catch (clearError) {
        console.error('❌ Error clearing storage:', clearError)
      }
      return { success: false, error: 'An unexpected error occurred during sign out' }
    }
  }

  /**
   * Reset password for web customer
   * 
   * @param {string} email - User email
   * @returns {Promise<Object>} Reset password result
   */
  export const resetPasswordWeb = async (email) => {
    try {
      // Trim and validate email
      const trimmedEmail = email.trim().toLowerCase()
      
      if (!trimmedEmail) {
        return { success: false, error: 'Email is required' }
      }

      console.log('Attempting to send password reset email to:', trimmedEmail)
      const resetUrl = `${window.location.origin}/reset-password`
      console.log('Redirect URL:', resetUrl)
      
      // Note: Add this URL in Supabase Dashboard → Authentication → URL Configuration → Redirect URLs
      const { data, error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: resetUrl,
      })

      if (error) {
        console.error('Reset password error:', error)
        console.error('Error details:', {
          message: error.message,
          status: error.status,
          name: error.name,
          code: error.code
        })
        
        // Provide more specific error messages
        if (error.code === 'over_email_send_rate_limit' || error.status === 429 || error.message?.includes('rate limit') || error.message?.includes('too many')) {
          return { 
            success: false, 
            error: 'Email rate limit exceeded. Please wait a few minutes before requesting another password reset email.',
            isRateLimited: true
          }
        } else if (error.message?.includes('email') && error.message?.includes('invalid')) {
          return { success: false, error: 'Invalid email address. Please check and try again.' }
        } else if (error.message?.includes('disabled') || error.message?.includes('not allowed')) {
          return { success: false, error: 'Password reset is currently disabled. Please contact support.' }
      }

        return { success: false, error: error.message || 'Failed to send reset email. Please check your email address and try again.' }
      }

      console.log('Password reset email sent successfully. Data:', data)
      // Note: Supabase returns success even if email doesn't exist (for security)
      // The email will only be sent if the account exists
      return { 
        success: true, 
        data,
        message: 'If an account exists with this email, you will receive a password reset link.'
      }
    } catch (error) {
      console.error('Unexpected error during password reset:', error)
      return { success: false, error: 'An unexpected error occurred. Please try again.' }
    }
  }

  /**
   * Update password for authenticated user
   * 
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Update password result
   */
  export const updatePasswordWeb = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) {
        console.error('Update password error:', error)
        return { success: false, error: error.message }
      }

      return { success: true, data }
    } catch (error) {
      console.error('Unexpected error during password update:', error)
      return { success: false, error: 'An unexpected error occurred' }
    }
  }

  /**
   * Check if current user is a web customer
   * 
   * @returns {Promise<boolean>} True if user is web customer
   */
  export const isWebCustomer = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) return false

      const role = user.user_metadata?.role || user.raw_user_meta_data?.role
      // Normalize: treat both 'customer' and 'web_customer' as customer
      return role === 'customer' || role === 'web_customer'
    } catch (error) {
      console.error('Error checking customer status:', error)
      return false
    }
  }

  /**
   * Reactivate a deactivated account
   * Sets account_status to 'active' in both profiles table and auth metadata
   * 
   * @param {string} userId - User ID to reactivate
   * @returns {Promise<Object>} Reactivation result
   */
  export const reactivateAccount = async (userId) => {
    try {
      if (!userId) {
        return { success: false, error: 'User ID is required' }
      }

      const timestamp = new Date().toISOString()

      // Update profile in database
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          account_status: 'active',
          deactivated_at: null,
          updated_at: timestamp,
        })
        .eq('id', userId)

      if (profileError) {
        console.error('Error reactivating account in profiles:', profileError)
        return { success: false, error: profileError.message || 'Failed to reactivate account' }
      }

      // Update auth metadata
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          account_status: 'active',
          deactivated_at: null,
        },
      })

      if (metadataError) {
        console.warn('Failed to update auth metadata for reactivation', metadataError)
        // Don't fail if metadata update fails - database update is more important
      }

      return { success: true, message: 'Account reactivated successfully' }
    } catch (error) {
      console.error('Error reactivating account:', error)
      return { success: false, error: error.message || 'An unexpected error occurred during reactivation' }
    }
  }

  /**
   * Deactivate current user's account
   * Sets account_status to 'inactive' in both profiles table and auth metadata
   * Requires password verification before deactivation
   * 
   * @returns {Promise<Object>} Deactivation result
   */
  export const deactivateAccount = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser()

      if (userError || !user) {
        return { success: false, error: 'No user found' }
      }

      const timestamp = new Date().toISOString()

      // Update profile in database
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          account_status: 'inactive',
          deactivated_at: timestamp,
          updated_at: timestamp,
        })
        .eq('id', user.id)

      if (profileError) {
        console.error('Error deactivating account in profiles:', profileError)
        return { success: false, error: profileError.message || 'Failed to deactivate account' }
      }

      // Update auth metadata
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          account_status: 'inactive',
          deactivated_at: timestamp,
        },
      })

      if (metadataError) {
        console.warn('Failed to update auth metadata for deactivation', metadataError)
        // Don't fail if metadata update fails - database update is more important
      }

      return { success: true, message: 'Account deactivated successfully' }
    } catch (error) {
      console.error('Error deactivating account:', error)
      return { success: false, error: error.message || 'An unexpected error occurred during deactivation' }
    }
  }

  /**
   * Get authentication state for customer
   * 
   * @returns {Promise<Object>} Auth state
   */
export const getWebAuthState = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const user = session?.user

    if (!user) {
      return {
        isAuthenticated: false,
        user: null,
        session: null,
        profile: null,
        role: null,
        isWebCustomer: false,
      }
    }

    const role = user.user_metadata?.role || user.raw_user_meta_data?.role
    // Normalize role: treat both 'customer' and 'web_customer' as 'customer'
    const normalizedRole = (role === 'web_customer' || role === 'customer') ? 'customer' : role

    const profile = await getCurrentWebProfile()

    return {
      isAuthenticated: true,
      user: user,
      session: session,
      profile: profile,
      role: normalizedRole || 'customer',
      isWebCustomer: normalizedRole === 'customer' || normalizedRole === 'web_customer',
    }
    } catch (error) {
      console.error('Error getting web auth state:', error)
      return {
        isAuthenticated: false,
        user: null,
        session: null,
        profile: null,
        role: null,
        isWebCustomer: false,
      }
    }
  }

  export default {
    signUpWebCustomer,
    signInWebCustomer,
    signInWithGoogleWeb,
    signInWithFacebookWeb,
    getOrCreateProfile,
    getCurrentWebProfile,
    signOutWeb,
    resetPasswordWeb,
    updatePasswordWeb,
    isWebCustomer,
    getWebAuthState,
    reactivateAccount,
    deactivateAccount,
  }
